import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X, Loader2 } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import Plate from "@/components/Plate";

export default function Plaques() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const reqIdRef = useRef(0);

  // Live, debounced search as the user types
  useEffect(() => {
    const query = q.trim();
    if (!query) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const myReq = ++reqIdRef.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/ventes/search/quick", { params: { q: query } });
        // Ignore stale responses
        if (myReq !== reqIdRef.current) return;
        setResults(data);
        setSearched(true);
      } catch {
        if (myReq !== reqIdRef.current) return;
        setResults([]);
        setSearched(true);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className="space-y-8">
      <header>
        <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
          Suivi véhicule
        </div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Recherche VO</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Cherchez un véhicule par <strong>plaque</strong>, <strong>marque</strong>, <strong>modèle</strong>, <strong>numéro VO</strong> ou nom du client. Les résultats s'affichent au fur et à mesure de la frappe.
        </p>
      </header>

      <div className="relative max-w-xl">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="plaque-search-input"
          type="search"
          name="plaque-search"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher…"
          className="pl-9 pr-9 rounded-sm h-11"
          autoFocus
        />
        {loading && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-klein animate-spin" />
        )}
        {!loading && q && (
          <button
            type="button"
            onClick={() => setQ("")}
            data-testid="plaque-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground hover:bg-slate-100 rounded-sm"
            aria-label="Effacer la recherche"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {searched && (
        <section className="space-y-4">
          <div className="text-sm text-muted-foreground" data-testid="plaque-result-count">
            {results.length} résultat(s) pour "{q}"
          </div>

          {results.length === 0 ? (
            <div className="bg-card border border-border rounded-sm p-10 text-center text-muted-foreground">
              Aucun véhicule trouvé. Essayez avec une autre orthographe.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Plaque</th>
                    <th className="px-6 py-3">N° VO</th>
                    <th className="px-6 py-3">Véhicule</th>
                    <th className="px-6 py-3">Client</th>
                    <th className="px-6 py-3 text-right">Prix</th>
                    <th className="px-6 py-3 text-right">Payé</th>
                    <th className="px-6 py-3 text-right">Restant</th>
                    <th className="px-6 py-3 text-center">Statut</th>
                    <th className="px-6 py-3 text-right">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((v) => (
                    <tr key={v.id} className="border-t hover:bg-slate-50">
                      <td className="px-6 py-3">{dateFR(v.date_vente)}</td>
                      <td className="px-6 py-3"><Plate value={v.plaque} small /></td>
                      <td className="px-6 py-3 font-mono text-xs">{v.numero_vo || "—"}</td>
                      <td className="px-6 py-3">{v.marque} {v.modele} {v.annee ? `(${v.annee})` : ""}</td>
                      <td className="px-6 py-3">{v.client_nom}</td>
                      <td className="px-6 py-3 text-right font-mono">{money(v.prix_vente)}</td>
                      <td className="px-6 py-3 text-right font-mono">{money(v.montant_paye)}</td>
                      <td className="px-6 py-3 text-right font-mono">{money(v.montant_restant)}</td>
                      <td className="px-6 py-3 text-center"><StatusBadge statut={v.statut} /></td>
                      <td className="px-6 py-3 text-right">
                        <Link to={`/ventes/${v.id}`} className="text-klein hover:underline text-xs" data-testid={`plaque-view-${v.id}`}>
                          Ouvrir →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
